import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications, type NotificationEvent } from '@/contexts/UserEventContext';
import { useAuthStore } from '@/stores/auth.store';

export function useMissionNotifications() {
  const { t } = useTranslation('mission');
  const navigate = useNavigate();
  const { notifications, markAsRead } = useNotifications();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const displayedNotificationIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      displayedNotificationIds.current.clear();
      return;
    }

    const newMissionNotifications = notifications.filter((notification) => {
      if (notification.read) {
        return false;
      }

      if (displayedNotificationIds.current.has(notification.id)) {
        return false;
      }

      if (notification.type !== 'notification.success') {
        return false;
      }

      // Detect mission-related notifications across supported languages.
      const data = notification.data as { title?: string; message?: string; actionUrl?: string };
      const isMissionNotification =
        (data.title?.includes('Mission') || data.title?.includes('mission')) ||
        (data.message?.includes('Mission') || data.message?.includes('mission'));

      return isMissionNotification;
    });

    newMissionNotifications.forEach((notification) => {
      displayedNotificationIds.current.add(notification.id);

      const data = notification.data as {
        title: string;
        message: string;
        actionUrl?: string;
        actionLabel?: string;
      };

      const isAllCompleted = data.title?.includes('All') || data.title?.includes('Complete');

      if (isAllCompleted) {
        toast.success(data.title, {
          description: data.message,
          duration: 6000,
          action: data.actionUrl
            ? {
                label: data.actionLabel || t('progress.viewDetails'),
                onClick: () => navigate(data.actionUrl!),
              }
            : undefined,
        });
      } else {
        toast.success(data.title, {
          description: data.message,
          duration: 4000,
          action: data.actionUrl
            ? {
                label: data.actionLabel || t('progress.viewDetails'),
                onClick: () => navigate(data.actionUrl!),
              }
            : undefined,
        });
      }

      markAsRead(notification.id);
    });
  }, [notifications, isAuthenticated, navigate, t, markAsRead]);
}

export function MissionNotificationListener() {
  useMissionNotifications();
  return null;
}
